
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { isTweet } from '@@/bits/post_attachment'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { LinkHelpers } from '@padlet/beethoven-client'
import { processedUrl } from '@padlet/vivaldi-client'
import {
  FallbackAttachmentType,
  fallbackAttachmentSizeMapping,
  fallbackImageUrl,
  getExtraMetadataText,
  getMetadataText,
  shouldDisplayMetadataTag,
} from 'bits/attachments'
import { getDisplayAttributes } from 'bits/beethoven'
import { PADLET_DOMAIN_REGEX } from 'bits/regex'
import { Component, Getter, Prop, State, Vue, Watch, defineAsyncComponent, namespace } from 'bits/vue'
import ImageThumbnail from 'library/v4/components/ImageThumbnail.vue'
import type { AttachmentProps, Cid, PostColor } from 'types'
import BeethovenAttachmentMetadataSlope from 'vuecomponents/beethoven-attachment-metadata-slope.vue'

function getStylizedProviderName(name): string {
  if (!name) return ''
  // This is done to comply with Youtube's requirements in order to use their player on Padlet.
  if (name.toLowerCase() === 'youtube') return name
  if (name.toLowerCase() === 'spotify') return name
  if (name.toLowerCase() === 'instagram') return name
  return name.toLowerCase()
}

/**
 * @displayName Beethoven Attachment Preview
 * Barebone component that fetches Beethoven Data and loads the preview image:
 * 1. Fetches preview info from beethoven.
 * 2. Preview Data is loaded, emit `load-data` then load image using ImageThumbnail
 * 3. Image is loaded, emit `load-image`
 */

@Component({
  components: {
    ImageThumbnail,
    BeethovenAttachmentMetadataSlope,
    SurfaceAttachmentTweet: defineAsyncComponent(() => import('vuecomponents/SurfaceAttachmentTweet.vue')),
  },
})
export default class BeethovenAttachmentPreview extends Vue {
  @Prop() url!: string
  @Prop({ default: 508 }) width!: number
  @Prop({ default: undefined }) height!: number | undefined
  @Prop({ required: true }) usageContext!: 'postModal' | 'postView' | 'postPreview'

  /**
   * Used to query attachment props belonging to a post.
   */
  @Prop({ required: true }) postCid!: Cid

  /**
   * Show border in case the preview is loaded on a background that requires it so that it won't blend in too much.
   * For example set this to true if loading a PDF on the white modal.
   */
  @Prop({ default: false }) xBorder!: boolean
  /**
   * Default to show metadata slope, can hide if needed
   */
  @Prop({ default: true }) xMetadata!: boolean
  /**
   * How the component should decide to show dark mode: Using browser or with custom isDark prop
   */
  @Prop({ default: 'browser' }) darkThemeType!: 'browser' | 'custom'
  @Prop({ default: 'browser' }) isDark!: false
  @Prop({ default: true }) lazyLoading!: boolean
  @Prop({ default: 'default' }) backgroundColor!: 'default' | NonNullable<PostColor>
  @Prop({ default: '' }) borderRadiusClass!: string
  @Prop({ default: false }) xHoverOverlay!: boolean

  @State dpr!: number
  @Getter dir!: string
  @Getter attachmentPropsFromPostPreview!: AttachmentProps | null
  @Getter('isStream') isWallInStreamFormat!: boolean
  @Getter('isCanvas') isWallInCanvasFormat!: boolean
  @PiniaMapState(useWindowSizeStore) isSmallerThanTabletPortrait!: ReturnType<
    typeof useWindowSizeStore
  >['isSmallerThanTabletPortrait']

  @PiniaMapState(useSurfaceDraftsStore) getAttachmentPropsFromDraft!: ReturnType<
    typeof useSurfaceDraftsStore
  >['getAttachmentPropsFromDraft']

  @namespace('post').Getter getAttachmentPropsFromPost!: (cid: Cid) => AttachmentProps | null
  @PiniaMapAction(useSurfaceAttachmentsStore) updateLinkDisplayAttributes!: (o: {
    url: string
    attributes: BeethovenDisplayAttributes
  }) => void

  @PiniaMapAction(useSurfaceDraftsStore) updateDraftWishContentAttachmentProps!: ReturnType<
    typeof useSurfaceDraftsStore
  >['updateDraftWishContentAttachmentProps']

  link: BeethovenDisplayAttributes | null = null
  isLoaded = false
  isError = false
  isImageLoaded = false

  // Note: need to initialize these values for reactivity of getter based on them to work
  imageUrl: string | null = null
  imageColor: string | null = null
  // Default to size of fallback image (images/fallback_file.png)
  thumbnailWidth = 3000
  thumbnailHeight = 1500

  get attachmentProps(): AttachmentProps | null {
    if (this.usageContext === 'postPreview') {
      return this.attachmentPropsFromPostPreview
    } else if (this.isUsingInPostModal) {
      return this.getAttachmentPropsFromDraft(this.postCid)
    } else {
      return this.getAttachmentPropsFromPost(this.postCid)
    }
  }

  get isUsingInPostModal(): boolean {
    return this.usageContext === 'postModal'
  }

  get isTweet(): boolean {
    return isTweet(this.attachmentProps)
  }

  get isLoadingFallbackImage(): boolean {
    if (this.isTweet) return false
    return (
      this.isError ||
      this.link?.is_fallback ||
      this.isFallbackPeekLink ||
      (!!this.imageUrl && this.imageUrl.startsWith('data:'))
    )
  }

  get isPreviewImageTransparent(): boolean {
    return this.link?.is_transparent || false
  }

  get previewImageSrc(): string {
    if (!!this.imageUrl && !this.isLoadingFallbackImage) return this.imageUrl
    const fallbackImgSrc = fallbackImageUrl(
      this.fallbackAttachmentType,
      this.isDark ? 'dark' : 'light',
      this.backgroundColor === 'default' ? null : this.backgroundColor,
    )
    if (this.isWallInCanvasFormat) return fallbackImgSrc
    return processedUrl(fallbackImgSrc, { width: this.fallbackWidth })
  }

  get fallbackWidth(): number {
    if (this.isWallInStreamFormat) return 1032 // 2 x 516
    return 476 // 2 x 28
  }

  get isCroppingVideoPreview(): boolean {
    return !!this.link && LinkHelpers.isVideo(this.link) && !!this.link.image_aspect_ratio
  }

  get videoCropRatio(): number {
    if (this.link?.provider_name.toLowerCase() === 'youtube') {
      return 16 / 9
    }
    return this.link?.image_aspect_ratio || 4 / 3
  }

  get isBrokenPadletLink(): boolean {
    if (!this.url) return false
    const regex = PADLET_DOMAIN_REGEX
    if (!regex.test(this.url)) return false
    return (
      !this.link ||
      (this.link.content_category === 'page' &&
        this.link.content_subcategory === 'padlet' &&
        !this.link.original_image_url)
    )
  }

  // See https://stackoverflow.com/c/padlet/questions/705 and resource_manager.rb#fallback_peek
  // This fallback image is shown everywhere outside of surface.
  // On surface, we want to show a different set of fallback images defined in bits/attachments.ts#fallbackImageUrl
  get isFallbackPeekLink(): boolean {
    const fallback_peek_filename = 'padlet_preview_1200x630'
    return !!this.imageUrl && this.imageUrl.includes(fallback_peek_filename)
  }

  get fallbackAttachmentType(): FallbackAttachmentType {
    if (this.isBrokenPadletLink || this.isFallbackPeekLink) return FallbackAttachmentType.BrokenPadletLink
    if (!this.link) return FallbackAttachmentType.BrokenLink
    if (LinkHelpers.isAudio(this.link)) return FallbackAttachmentType.Audio
    if (LinkHelpers.isVideo(this.link)) return FallbackAttachmentType.Video
    if (LinkHelpers.isPhoto(this.link)) return FallbackAttachmentType.Image
    if (LinkHelpers.isFile(this.link)) return FallbackAttachmentType.File
    if (LinkHelpers.isExcelSpreadsheet(this.link)) return FallbackAttachmentType.Spreadsheet
    if (LinkHelpers.isPowerpointPresentation(this.link)) return FallbackAttachmentType.Presentation
    if (LinkHelpers.isGoogleDoc(this.link)) return FallbackAttachmentType.GoogleDocument
    if (LinkHelpers.isDocument(this.link)) return FallbackAttachmentType.TextDocument
    if (LinkHelpers.isPage(this.link)) return FallbackAttachmentType.Link
    return FallbackAttachmentType.Link
  }

  get hasMetadata(): boolean {
    if (!this.link) return false
    return shouldDisplayMetadataTag(this.link) && this.isImageLoaded
  }

  get metadataText(): string {
    if (!this.link) return ''
    return getMetadataText(this.link)
  }

  get extraMetadataText(): string {
    if (!this.link) {
      return ''
    }

    return getExtraMetadataText(this.link)
  }

  get padletAttachmentBottomBorderRadiusStyle(): Record<string, string> {
    // We used to have the exaggerated bottom border radius for padlet attachments everywhere
    // but we moved the attachment remove button in post modal to the bottom => stop doing that for the post modal
    if ((!!this.link && !LinkHelpers.isPadletWall(this.link)) || this.usageContext === 'postModal') {
      return {}
    }

    const borderRadiusAttribute = this.dir === 'rtl' ? 'borderBottomLeftRadius' : 'borderBottomRightRadius'

    return {
      [borderRadiusAttribute]: `${Math.floor((this.width * 20) / 100)}px`,
    }
  }

  @Watch('width')
  handleWidthChanged(): void {
    this.getBeethovenAttributes()
  }

  @Watch('url')
  handleAttachmentUrlChanged(): void {
    this.getBeethovenAttributes()
  }

  useSizeDataForFallbackImage(): void {
    this.isImageLoaded = true
    const fallbackImageSize = fallbackAttachmentSizeMapping[this.fallbackAttachmentType]
    this.thumbnailWidth = fallbackImageSize.width
    this.thumbnailHeight = fallbackImageSize.height
  }

  handleBeethovenError(error): void {
    this.$emit('error', error)
    this.isError = true
    this.useSizeDataForFallbackImage()
  }

  async getBeethovenAttributes(): Promise<void> {
    const locale = 'en'
    const bhOptions = {
      dpr: this.dpr,
      locale,
      width: this.width,
      height: this.height,
    }

    this.isError = false

    let data
    try {
      data = await getDisplayAttributes(this.url, bhOptions)
      if (data === null) {
        this.handleBeethovenError('No data.')
      }
      if (data.error) {
        this.handleBeethovenError(data.error)
      }
    } catch (error) {
      this.handleBeethovenError(error)
    }

    if (this.isError) return

    this.link = data
    this.updateLinkDisplayAttributes({ url: this.url, attributes: data })
    this.updateDraftWishContentAttachmentProps({
      cid: this.postCid,
      url: this.url,
      attachmentProps: data.metadata?.attachment_props,
    })
    if (data.metadata?.attachment_props?.type !== 'tweet') {
      // For tweets, we don't want to set the loaded state
      // yet when LinksController API result comes. This is
      // to prevent a case when a user publishes the post
      // before the link extraction completes, causing the
      // default preview to be shown briefly before a realtime
      // message comes and the tweet preview is rendered.
      this.isLoaded = true
      this.$emit('load-data', data)
    }
    this.imageUrl = data.image_url
    this.imageColor = data.image_color
    this.thumbnailWidth = data.image_width
    this.thumbnailHeight = data.image_height
    if (this.isLoadingFallbackImage) {
      this.useSizeDataForFallbackImage()
    }
  }

  handleImageLoaded(): void {
    this.isImageLoaded = true
    this.$emit('load-image')
  }

  mounted(): void {
    // If we receive attachment props of a tweet,
    // we don't need to call Links API anymore.
    // Attachment props should have enough data
    // to render the tweet.
    if (isTweet(this.attachmentProps)) {
      // However, we should still emit data as if
      // we received some data from Links API so
      // the parent component's behavior isn't
      // broken.
      this.isLoaded = true
      this.$emit('load-data', {
        url: this.url,
        provider_name: 'Twitter',
        content_category: 'page',
        content_subcategory: 'html',
        image_color: '',
      })
    } else {
      this.getBeethovenAttributes()
    }
  }
}
